import {isAxiosError, type AxiosError} from "axios";

import axios from "./axios";

export type BroadcastingConfig = {
  key: string;
  host: string;
  ws_port: number;
  wss_port: number;
  force_tls: boolean;
  ws_path: string;
  auth_endpoint: string;
};

function pickString(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function pickNumber(v: unknown, fallback: number): number {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export function parseBroadcastingConfig(raw: unknown): BroadcastingConfig {
  const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const key = pickString(o.key);
  const host = pickString(o.host);
  const ws_path = pickString(o.ws_path ?? o.wsPath);
  const auth_endpoint = pickString(o.auth_endpoint ?? o.authEndpoint);
  const force_tls = Boolean(o.force_tls ?? o.forceTLS ?? true);
  const ws_port = pickNumber(o.ws_port ?? o.wsPort, 80);
  const wss_port = pickNumber(o.wss_port ?? o.wssPort, 443);

  if (!key || !host || !ws_path || !auth_endpoint) {
    throw new Error("Invalid broadcasting config");
  }

  return {key, host, ws_port, wss_port, force_tls, ws_path, auth_endpoint};
}

const LOOPBACK_HOST_PATTERN = /^(127\.0\.0\.1|localhost|::1)$/i;

function readClientSocketUrl(): URL | null {
  const raw = import.meta.env.VITE_SOCKET_URL;
  if (typeof raw !== "string" || !raw.trim()) return null;
  try {
    return new URL(raw);
  } catch {
    return null;
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function readApiOriginHost(): string | null {
  for (const raw of [import.meta.env.VITE_API_DEV_URL, import.meta.env.VITE_API_URL]) {
    if (typeof raw !== "string" || !/^https?:\/\//i.test(raw)) continue;
    try {
      return new URL(raw).hostname;
    } catch {
      continue;
    }
  }
  return null;
}

function readApiUsesTls(): boolean {
  for (const raw of [import.meta.env.VITE_API_DEV_URL, import.meta.env.VITE_API_URL]) {
    if (typeof raw === "string" && /^https:\/\//i.test(raw)) return true;
    if (typeof raw === "string" && /^http:\/\//i.test(raw)) return false;
  }
  return true;
}

function configFromSocketUrl(socketUrl: URL, config: BroadcastingConfig): BroadcastingConfig {
  const force_tls = socketUrl.protocol === "wss:";
  const explicitPort = socketUrl.port ? Number(socketUrl.port) : null;
  let ws_path = socketUrl.pathname.replace(/\/$/, "") || config.ws_path;

  return {
    ...config,
    host: socketUrl.hostname,
    force_tls,
    ws_port: force_tls ? 80 : (explicitPort ?? 80),
    wss_port: force_tls ? (explicitPort ?? 443) : 443,
    ws_path,
  };
}

/** API may return loopback or internal host; prefer `VITE_SOCKET_URL` when the browser cannot reach it. */
export function resolveBroadcastingConfigForClient(config: BroadcastingConfig): BroadcastingConfig {
  const clientSocketUrl = readClientSocketUrl();

  if (LOOPBACK_HOST_PATTERN.test(config.host)) {
    if (clientSocketUrl) {
      return configFromSocketUrl(clientSocketUrl, config);
    }

    if (!import.meta.env.DEV) {
      return config;
    }

    return {
      ...config,
      host: readApiOriginHost() ?? "dev.stars-pro.com",
      force_tls: readApiUsesTls(),
      ws_port: 80,
      wss_port: 443,
    };
  }

  if (clientSocketUrl && !import.meta.env.DEV) {
    return configFromSocketUrl(clientSocketUrl, config);
  }

  return config;
}

/** Reverb/Pusher-style URL: `{ws_path}/app/{key}` + query (not a bare `/ws`). */
export function buildPusherWebSocketUrl(c: BroadcastingConfig): string {
  const scheme = c.force_tls ? "wss" : "ws";
  const port = c.force_tls ? c.wss_port : c.ws_port;
  const defaultPort = c.force_tls ? 443 : 80;

  let path = c.ws_path.startsWith("/") ? c.ws_path : `/${c.ws_path}`;
  if (path.endsWith("/")) path = path.slice(0, -1);
  const appPath = `${path}/app/${encodeURIComponent(c.key)}`;
  const query = "protocol=7&client=js&version=8.4.0&flash=false";
  const hostPort = port && port !== defaultPort ? `${c.host}:${port}` : c.host;

  return `${scheme}://${hostPort}${appPath}?${query}`;
}

const BROADCASTING_CONFIG_MAX_ATTEMPTS = 3;

export type BroadcastingConfigFetchResult = {
  config: BroadcastingConfig | null;
  attempts: number;
  lastError?: unknown;
  lastAxiosError?: AxiosError;
};

export async function fetchBroadcastingConfig(): Promise<BroadcastingConfigFetchResult> {
  let lastError: unknown;
  let lastAxiosError: AxiosError | undefined;

  for (let attempt = 0; attempt < BROADCASTING_CONFIG_MAX_ATTEMPTS; attempt++) {
    try {
      const {data} = await axios.get<unknown>("broadcasting/config");
      const payload =
        data && typeof data === "object" && "data" in data ? (data as {data: unknown}).data : data;
      return {
        config: resolveBroadcastingConfigForClient(parseBroadcastingConfig(payload)),
        attempts: attempt + 1,
      };
    } catch (error) {
      lastError = error;
      if (isAxiosError(error)) {
        lastAxiosError = error;
      }
      if (attempt >= BROADCASTING_CONFIG_MAX_ATTEMPTS - 1) {
        break;
      }
      await delay(400 * (attempt + 1));
    }
  }

  return {
    config: null,
    attempts: BROADCASTING_CONFIG_MAX_ATTEMPTS,
    lastError,
    lastAxiosError,
  };
}

/**
 * Channel auth must hit the exact URL from `GET /api/v1/broadcasting/config` (`auth_endpoint`).
 * Axios cannot use path-only URLs (it joins them onto `…/api/v1`). On Vite dev, same-host paths
 * are rewritten to the dev-server origin so `/API` proxies to Laravel.
 */
export function resolveBroadcastingAuthRequestUrl(authEndpoint: string): string {
  if (/^https?:\/\//i.test(authEndpoint)) {
    try {
      const parsed = new URL(authEndpoint);
      const apiUrl = import.meta.env.VITE_API_URL;

      if (typeof window !== "undefined") {
        if (parsed.host === window.location.host) {
          return `${window.location.origin}${parsed.pathname}${parsed.search}`;
        }

        if (typeof apiUrl === "string" && apiUrl.startsWith("/")) {
          return `${window.location.origin}${parsed.pathname}${parsed.search}`;
        }
      }

      return authEndpoint;
    } catch {
      return authEndpoint;
    }
  }

  if (typeof window !== "undefined") {
    const path = authEndpoint.startsWith("/") ? authEndpoint : `/${authEndpoint}`;
    return `${window.location.origin}${path}`;
  }

  return authEndpoint;
}

/** @deprecated Use resolveBroadcastingAuthRequestUrl — kept for callers/tests expecting path-only. */
export function normalizeBroadcastingAuthEndpoint(authEndpoint: string): string {
  const resolved = resolveBroadcastingAuthRequestUrl(authEndpoint);
  if (/^https?:\/\//i.test(resolved)) {
    try {
      return new URL(resolved).pathname;
    } catch {
      return authEndpoint;
    }
  }
  return resolved;
}

function readApiFailureMessage(payload: unknown): string | null {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const root = payload as Record<string, unknown>;
  const failed =
    root.success === 0 ||
    root.success === false ||
    root.status === "error" ||
    root.status === "fail" ||
    root.status === "failed";

  if (!failed) {
    return null;
  }

  const message = typeof root.message === "string" ? root.message.trim() : "";
  return message || "Broadcasting auth rejected";
}

function extractBroadcastAuth(payload: unknown, depth = 0): string {
  if (depth > 4 || payload == null) {
    return "";
  }

  if (typeof payload === "string") {
    const trimmed = payload.trim();
    if (!trimmed) {
      return "";
    }

    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        return extractBroadcastAuth(JSON.parse(trimmed), depth + 1);
      } catch {
        return trimmed.includes(":") ? trimmed : "";
      }
    }

    return trimmed.includes(":") ? trimmed : "";
  }

  if (typeof payload !== "object" || Array.isArray(payload)) {
    return "";
  }

  const root = payload as Record<string, unknown>;

  for (const [key, value] of Object.entries(root)) {
    if (key.toLowerCase() === "auth" && typeof value === "string" && value.trim().length > 0) {
      return value;
    }
  }

  for (const value of Object.values(root)) {
    const nested = extractBroadcastAuth(value, depth + 1);
    if (nested) {
      return nested;
    }
  }

  return "";
}

function isHtmlPayload(payload: unknown): boolean {
  if (typeof payload !== "string") {
    return false;
  }
  const trimmed = payload.trim().toLowerCase();
  return trimmed.startsWith("<!doctype") || trimmed.startsWith("<html");
}

function assertBroadcastingAuthPayload(payload: unknown): void {
  if (isHtmlPayload(payload)) {
    throw new Error(
      "Broadcasting auth returned HTML — auth_endpoint likely hit the SPA shell, not Laravel",
    );
  }
}

function formatBroadcastingAuthDebugHint(payload: unknown): string {
  if (payload == null) {
    return "empty response body";
  }

  if (typeof payload === "object" && !Array.isArray(payload)) {
    return `response keys: ${Object.keys(payload as Record<string, unknown>).join(", ") || "(none)"}`;
  }

  return `response type: ${typeof payload}`;
}

export async function fetchPrivateChannelAuth(
  authEndpoint: string,
  channelName: string,
  socketId: string,
): Promise<string> {
  const url = resolveBroadcastingAuthRequestUrl(authEndpoint);
  const {data} = await axios.post<unknown>(
    url,
    {
      socket_id: socketId,
      channel_name: channelName,
    },
    {
      headers: {
        Accept: "application/json",
      },
    },
  );

  assertBroadcastingAuthPayload(data);

  const apiFailure = readApiFailureMessage(data);
  if (apiFailure) {
    throw new Error(apiFailure);
  }

  const auth = extractBroadcastAuth(data);
  if (!auth) {
    const hint = formatBroadcastingAuthDebugHint(data);
    throw new Error(`Broadcasting auth response missing auth (${hint})`);
  }
  return auth;
}

function waitForSocketOpen(ws: WebSocket, maxMs = 15000): Promise<void> {
  if (ws.readyState === WebSocket.OPEN) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const t = window.setTimeout(() => reject(new Error("WebSocket open timeout")), maxMs);
    ws.addEventListener(
      "open",
      () => {
        window.clearTimeout(t);
        resolve();
      },
      {once: true},
    );
    ws.addEventListener(
      "error",
      () => {
        window.clearTimeout(t);
        reject(new Error("WebSocket connection error"));
      },
      {once: true},
    );
  });
}

type PusherFrame = {event: string; data?: string};

function waitForPusherSocketId(ws: WebSocket, maxMs = 10000): Promise<string> {
  return new Promise((resolve, reject) => {
    const t = window.setTimeout(() => reject(new Error("pusher socket_id timeout")), maxMs);
    const onMessage = (event: MessageEvent) => {
      try {
        const payload = JSON.parse(event.data as string) as PusherFrame;
        if (payload.event === "pusher:connection_established" && payload.data) {
          const inner = JSON.parse(payload.data) as {socket_id?: string};
          if (inner.socket_id) {
            window.clearTimeout(t);
            ws.removeEventListener("message", onMessage);
            resolve(inner.socket_id);
          }
        }
      } catch {
        /* ignore malformed frames */
      }
    };
    ws.addEventListener("message", onMessage);
  });
}

const IGNORED_PUSHER_EVENTS = new Set([
  "pusher:connection_established",
  "pusher_internal:subscription_succeeded",
  "pusher:pong",
]);

export function attachPusherEventDispatcher(ws: WebSocket): void {
  ws.onmessage = (event: MessageEvent) => {
    try {
      const payload = JSON.parse(event.data as string) as PusherFrame;
      if (IGNORED_PUSHER_EVENTS.has(payload.event)) return;
      const customEvent = new CustomEvent(payload.event, {
        detail: payload.data ? JSON.parse(payload.data) : undefined,
      });
      window.dispatchEvent(customEvent);
    } catch {
      /* ignore */
    }
  };
}

export async function openAuthenticatedPusherWebSocket(
  config: BroadcastingConfig,
  privateChannelName: string,
): Promise<WebSocket> {
  const url = buildPusherWebSocketUrl(config);
  const ws = new WebSocket(url);

  const socketIdPromise = waitForPusherSocketId(ws);
  await waitForSocketOpen(ws);
  const socketId = await socketIdPromise;

  const auth = await fetchPrivateChannelAuth(config.auth_endpoint, privateChannelName, socketId);
  ws.send(
    JSON.stringify({
      event: "pusher:subscribe",
      data: {channel: privateChannelName, auth},
    }),
  );

  attachPusherEventDispatcher(ws);
  return ws;
}
